import React, { useEffect, useMemo, useState } from 'react';
import { Alert, Box, Breadcrumbs, Button, Link, MenuItem, Paper, Snackbar, TextField, Typography } from '@mui/material';
import { Link as RouterLink, useNavigate, useParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import clientProfileService from '../services/ClientProfileService';
import planService from '../services/PlanService';

function normalizeString(value) {
    return String(value ?? '').trim();
}

function AdminClientProfile({ embedded = false }) {
    const { t } = useTranslation();
    const { clientId } = useParams();
    const navigate = useNavigate();

    const [loading, setLoading] = useState(false);
    const [saving, setSaving] = useState(false);

    const [plans, setPlans] = useState([]);

    const [planId, setPlanId] = useState('');
    const [clientName, setClientName] = useState('');
    const [companyName, setCompanyName] = useState('');
    const [email, setEmail] = useState('');
    const [phone, setPhone] = useState('');
    const [address, setAddress] = useState('');

    const [snackbar, setSnackbar] = useState({ open: false, message: '', severity: 'success' });

    const canSave = useMemo(() => {
        return Boolean(clientId) && !loading && !saving;
    }, [clientId, loading, saving]);

    useEffect(() => {
        const load = async () => {
            if (!clientId) return;
            setLoading(true);
            try {
                const [profile, plansData] = await Promise.all([
                    clientProfileService.getClientProfile(clientId),
                    planService.getPlans(),
                ]);

                setPlans(Array.isArray(plansData) ? plansData : []);

                setPlanId(profile?.planId || '');
                setClientName(profile?.clientName || '');
                setCompanyName(profile?.companyName || '');
                setEmail(profile?.email || '');
                setPhone(profile?.phone || '');
                setAddress(profile?.address || '');
            } catch (e) {
                console.error('Failed to load client profile', e);
                setSnackbar({ open: true, message: t('adminClientProfile.loadFailed', 'Failed to load client profile'), severity: 'error' });

                // Still try to load plans so the selector works.
                try {
                    const plansData = await planService.getPlans();
                    setPlans(Array.isArray(plansData) ? plansData : []);
                } catch (e2) {
                    console.error('Failed to load plans', e2);
                }

                // Default clientName to clientId for convenience.
                setClientName('');
            } finally {
                setLoading(false);
            }
        };

        load();
    }, [clientId]);

    const handleSave = async () => {
        if (!clientId) return;

        setSaving(true);
        try {
            const payload = {
                clientId,
                planId: normalizeString(planId),
                clientName: normalizeString(clientName),
                companyName: normalizeString(companyName),
                email: normalizeString(email),
                phone: normalizeString(phone),
                address: normalizeString(address),
            };

            const updated = await clientProfileService.upsertClientProfile(clientId, payload);

            setPlanId(updated?.planId || payload.planId);
            setClientName(updated?.clientName || payload.clientName);
            setCompanyName(updated?.companyName || payload.companyName);
            setEmail(updated?.email || payload.email);
            setPhone(updated?.phone || payload.phone);
            setAddress(updated?.address || payload.address);

            setSnackbar({ open: true, message: t('adminClientProfile.saved', 'Client profile saved'), severity: 'success' });
        } catch (e) {
            console.error('Failed to save client profile', e);
            setSnackbar({ open: true, message: t('adminClientProfile.saveFailed', 'Failed to save client profile'), severity: 'error' });
        } finally {
            setSaving(false);
        }
    };

    const handleCloseSnackbar = () => setSnackbar((s) => ({ ...s, open: false }));

    const sortedPlans = useMemo(() => {
        const list = Array.isArray(plans) ? [...plans] : [];
        list.sort((a, b) => normalizeString(a?.planName).localeCompare(normalizeString(b?.planName)));
        return list;
    }, [plans]);

    return (
        <Box sx={{ p: embedded ? 0 : 2, display: 'flex', flexDirection: 'column', gap: 2 }}>
            {!embedded ? (
                <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 2, flexWrap: 'wrap' }}>
                    <Box>
                        <Breadcrumbs aria-label={t('adminClientProfile.breadcrumb', 'breadcrumb')} sx={{ mb: 0.5 }}>
                            <Link
                                component={RouterLink}
                                underline="hover"
                                color="inherit"
                                to="/customer-list"
                                sx={{ fontSize: 13 }}
                            >
                                {t('adminClientProfile.clientList', 'Client List')}
                            </Link>
                            <Typography color="text.primary" sx={{ fontSize: 13 }}>
                                {t('adminClientProfile.profile', 'Profile')}
                            </Typography>
                        </Breadcrumbs>
                        <Typography variant="h5" fontWeight="bold">{t('adminClientProfile.clientProfile', 'Client Profile')}</Typography>
                    </Box>
                    <Button variant="outlined" onClick={() => navigate('/customer-list')} disabled={saving}>
                        {t('adminClientProfile.backToClients', 'Back to Clients')}
                    </Button>
                </Box>
            ) : null}

            <Paper sx={{ p: 2 }} elevation={1}>
                <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                    {t('adminClientProfile.clientId', 'Client ID: {{id}}', { id: clientId || '-' })}
                </Typography>

                {loading ? <Alert severity="info" sx={{ mb: 2 }}>{t('adminClientProfile.loading', 'Loading…')}</Alert> : null}

                <TextField
                    label={t('adminClientProfile.plan', 'Plan')}
                    select
                    fullWidth
                    size="small"
                    sx={{ mb: 2 }}
                    value={planId}
                    onChange={(e) => setPlanId(e.target.value)}
                    disabled={loading || saving}
                >
                    <MenuItem value="">
                        {t('adminClientProfile.none', '(none)')}
                    </MenuItem>
                    {sortedPlans.map((p) => (
                        <MenuItem key={p.planId} value={p.planId}>
                            {p.planName || p.planId}
                        </MenuItem>
                    ))}
                </TextField>

                <TextField
                    label={t('adminClientProfile.clientName', 'Client Name')}
                    fullWidth
                    size="small"
                    sx={{ mb: 2 }}
                    value={clientName}
                    onChange={(e) => setClientName(e.target.value)}
                    disabled={loading || saving}
                />

                <TextField
                    label={t('adminClientProfile.companyName', 'Company Name')}
                    fullWidth
                    size="small"
                    sx={{ mb: 2 }}
                    value={companyName}
                    onChange={(e) => setCompanyName(e.target.value)}
                    disabled={loading || saving}
                />

                <TextField
                    label={t('adminClientProfile.email', 'Email')}
                    fullWidth
                    size="small"
                    sx={{ mb: 2 }}
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    disabled={loading || saving}
                />

                <TextField
                    label={t('adminClientProfile.phone', 'Phone')}
                    fullWidth
                    size="small"
                    sx={{ mb: 2 }}
                    value={phone}
                    onChange={(e) => setPhone(e.target.value)}
                    disabled={loading || saving}
                />

                <TextField
                    label={t('adminClientProfile.address', 'Address')}
                    fullWidth
                    size="small"
                    multiline
                    minRows={3}
                    sx={{ mb: 2 }}
                    value={address}
                    onChange={(e) => setAddress(e.target.value)}
                    disabled={loading || saving}
                />

                <Box sx={{ display: 'flex', justifyContent: 'flex-end' }}>
                    <Button variant="contained" onClick={handleSave} disabled={!canSave}>
                        {t('adminClientProfile.save', 'Save')}
                    </Button>
                </Box>
            </Paper>

            <Snackbar open={snackbar.open} autoHideDuration={3000} onClose={handleCloseSnackbar}>
                <Alert onClose={handleCloseSnackbar} severity={snackbar.severity} sx={{ width: '100%' }}>
                    {snackbar.message}
                </Alert>
            </Snackbar>
        </Box>
    );
}

export default AdminClientProfile;
